/**
 * nj-typed-location: a typed event address that verifies becomes a saved location in one step (CC 0402).
 *
 * The public venue map reads coordinates only from a saved location linked to the event, so a venue
 * typed into an event never got a map. On a verified answer (with coordinates) this creates the location
 * through the call Settings › Locations uses (createLocation), with the verified address, coordinates and
 * time zone, and returns it so the designer links the event to it. A failed verification creates nothing.
 */
import { createLocation, validateAddress } from "@v0y4ge/api-client";
import { LOCATIONS_OPTS } from "../../shell/request-opts";

export type TypedLocationResult =
  | { kind: "saved"; locationId: string; name: string; address: string; timezone: string | null }
  | { kind: "invalid"; reason: string }
  | { kind: "save-failed"; reason: string };

type Deps = {
  validate?: typeof validateAddress;
  create?: typeof createLocation;
  geoOpts: { baseUrl: string; surface?: string };
};

/** Check `address` only (CC 0421: the blur feedback). Valid means it verified with coordinates, so a Save
 *  can make it a location. Creates nothing. */
export async function checkTypedAddress(address: string, deps: Pick<Deps, "validate" | "geoOpts">): Promise<{ valid: boolean; reason?: string }> {
  const validate = deps.validate ?? validateAddress;
  try {
    const res = await validate({ address: address.trim() } as Parameters<typeof validateAddress>[0], deps.geoOpts as Parameters<typeof validateAddress>[1]);
    return res?.valid && typeof res.lat === "number" && typeof res.lng === "number" ? { valid: true } : { valid: false, reason: res?.reason ?? "" };
  } catch (e) {
    return { valid: false, reason: e instanceof Error ? e.message : String(e) };
  }
}

/** Verify `address`; when it verifies with coordinates, create the location and return it. Since CC 0421 this
 *  runs at Save, with the venue name and address as they are then. */
export async function verifyAndSaveTypedAddress(address: string, venueName: string, deps: Deps): Promise<TypedLocationResult> {
  const validate = deps.validate ?? validateAddress;
  const create = deps.create ?? createLocation;
  const q = address.trim();
  let res: Awaited<ReturnType<typeof validateAddress>>;
  try {
    res = await validate({ address: q } as Parameters<typeof validateAddress>[0], deps.geoOpts as Parameters<typeof validateAddress>[1]);
  } catch (e) {
    return { kind: "invalid", reason: e instanceof Error ? e.message : String(e) };
  }
  if (!res?.valid || typeof res.lat !== "number" || typeof res.lng !== "number") {
    return { kind: "invalid", reason: res?.reason ?? "" };
  }
  const n = res.normalized;
  const label = (res.label || q).trim();
  const name = (venueName.trim() || n?.street || label).slice(0, 120);
  try {
    const created = await create({
      name,
      address: (n?.street || label).slice(0, 240) || null,
      addressLine2: null,
      city: n?.city || null,
      region: n?.region || null,
      postalCode: n?.postalCode || null,
      country: n?.country || null,
      latitude: res.lat,
      longitude: res.lng,
      timezone: res.tz || null,
      active: true,
    } as Parameters<typeof createLocation>[0], LOCATIONS_OPTS);
    const id = String((created as { location?: { id?: string } })?.location?.id ?? "");
    if (!id) return { kind: "save-failed", reason: "the location was created without an id" };
    return { kind: "saved", locationId: id, name, address: label, timezone: res.tz || null };
  } catch (e) {
    return { kind: "save-failed", reason: e instanceof Error ? e.message : String(e) };
  }
}
